// vg-binding: P7.D-23
//
// apps/vendor/src/features/products/add/FileImporter.tsx
//
// File import for the Add Product page (rebuilt 2026-10-13; plan
// docs/plans/2026-10-13-vendor-import-mapping.md).
//
// One row = one variant; rows sharing `product_code` form one product, and
// every variant names the Printway SKU it fulfils. Three steps:
//   1. pick a .csv/.xlsx — checked locally (type, size, rows, header);
//   2. "Check file" — a server dry run lists every row error / warning and
//      the Printway product each group matched; nothing is written;
//   3. "Import" — enabled only with zero errors; all-or-nothing on the
//      server. A 422 there (something changed since the check) redraws the
//      table from the server's answer.

import { useRef, useState, type JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button, Spinner, cn, useToast } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';
import {
  ImportRejectedError,
  VENDOR_PRODUCT_LIST_KEY,
  fetchPrintwaySkus,
  postProductImport,
  type ImportReport,
} from '../api.js';
import {
  MAX_ROWS,
  REQUIRED_COLUMNS,
  TEMPLATE_COLUMNS,
  buildTemplate,
  readImportFile,
  type ImportRow,
  type SheetProblem,
} from './importSheet.js';

interface Props {
  vendorId: string;
}

function save(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function FileImporter(_props: Props): JSX.Element {
  const { t } = useTranslation('products');
  const { toast } = useToast();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ImportRow[] | null>(null);
  const [problem, setProblem] = useState<{ code: SheetProblem; columns?: string[] } | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [templateBusy, setTemplateBusy] = useState<'csv' | 'xlsx' | null>(null);
  // CR5-01 — the file is parsed in a worker (up to PARSE_TIMEOUT_MS): show it.
  const [reading, setReading] = useState(false);

  const reset = () => {
    setRows(null);
    setReport(null);
    setProblem(null);
  };

  const check = useMutation({
    mutationFn: (r: ImportRow[]) => postProductImport(r, true),
    onSuccess: (data) => setReport(data),
    onError: () => {
      setReport(null);
      toast({ variant: 'error', title: t('fileImporter.checkFailed') });
    },
  });

  const commit = useMutation({
    mutationFn: (r: ImportRow[]) => postProductImport(r, false),
    onSuccess: async (data) => {
      toast({
        variant: 'success',
        title: t('fileImporter.importedToast', { products: data.imported_products, variants: data.imported_variants }),
      });
      setFileName(null);
      reset();
      await qc.invalidateQueries({ queryKey: VENDOR_PRODUCT_LIST_KEY, refetchType: 'all' });
    },
    onError: (err) => {
      if (err instanceof ImportRejectedError) setReport(err.report);
      toast({ variant: 'error', title: t('fileImporter.importFailed') });
    },
  });

  async function onPick(file: File): Promise<void> {
    reset();
    setFileName(file.name);
    setReading(true);
    let res: Awaited<ReturnType<typeof readImportFile>>;
    try {
      res = await readImportFile(file);
    } finally {
      setReading(false);
    }
    if (!res.ok) {
      setProblem({ code: res.problem, ...(res.columns ? { columns: res.columns } : {}) });
      if (res.problem === 'READ_FAILED' || res.problem === 'PARSE_TIMEOUT') {
        toast({ variant: 'error', title: t(`fileImporter.problems.${res.problem}`) });
      }
      return;
    }
    setRows(res.rows);
    check.mutate(res.rows);
  }

  async function downloadTemplate(kind: 'csv' | 'xlsx'): Promise<void> {
    setTemplateBusy(kind);
    try {
      const skus = kind === 'xlsx' ? await fetchPrintwaySkus() : (await fetchPrintwaySkus()).slice(0, 2);
      const guide: Array<[string, string]> = TEMPLATE_COLUMNS.map((c) => [c, t(`fileImporter.guide.${c}`)]);
      guide.unshift([t('fileImporter.guide.columnHeader'), t('fileImporter.guide.meaningHeader')]);
      save(buildTemplate({ kind, skus, guide }), `printway-product-import.${kind}`);
    } catch {
      toast({ variant: 'error', title: t('fileImporter.templateFailed') });
    } finally {
      setTemplateBusy(null);
    }
  }

  const errors = report?.errors ?? [];
  const warnings = report?.warnings ?? [];
  const busy = reading || check.isPending || commit.isPending;
  const canImport = !!rows && !!report && errors.length === 0 && !busy;
  const issuesByRow = new Map<number, Array<{ kind: 'error' | 'warning'; text: string }>>();
  for (const e of errors) {
    const list = issuesByRow.get(e.row) ?? [];
    list.push({ kind: 'error', text: t(`fileImporter.codes.${e.code}`, { field: e.field }) });
    issuesByRow.set(e.row, list);
  }
  for (const w of warnings) {
    const list = issuesByRow.get(w.row) ?? [];
    list.push({ kind: 'warning', text: t('fileImporter.codes.LABEL_MISMATCH', { field: w.field, value: w.printway_value }) });
    issuesByRow.set(w.row, list);
  }
  const blueprintOf = new Map((report?.products ?? []).map((p) => [p.product_code, p.blueprint_name]));

  return (
    <section data-testid="file-importer" className="rounded-xl border border-pw-border bg-pw-bg p-4">
      <h3 className="text-sm font-semibold text-pw-text">{t('fileImporter.title')}</h3>
      <p className="mt-1 text-xs text-pw-text-muted">{t('fileImporter.intro', { max: MAX_ROWS })}</p>
      <p className="mt-1 text-xs text-pw-text-muted">
        {t('fileImporter.requiredColumns')} <code>{REQUIRED_COLUMNS.join(', ')}</code>
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          data-testid="add-product-file-upload"
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onPick(file);
            if (inputRef.current) inputRef.current.value = '';
          }}
        />
        <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="file-importer-pick">
          {t('fileImporter.pickFile')}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => void downloadTemplate('xlsx')}
          disabled={templateBusy !== null}
          data-testid="add-product-file-download-template"
        >
          {templateBusy === 'xlsx' ? <Spinner size="sm" /> : null}
          {t('fileImporter.downloadXlsx')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => void downloadTemplate('csv')}
          disabled={templateBusy !== null}
          data-testid="add-product-file-download-template-csv"
        >
          {templateBusy === 'csv' ? <Spinner size="sm" /> : null}
          {t('fileImporter.downloadCsv')}
        </Button>
        {fileName ? (
          <span className="text-xs text-pw-text-muted" data-testid="file-importer-filename">
            {t('fileImporter.selectedPrefix')} <code>{fileName}</code>
          </span>
        ) : null}
      </div>

      {problem ? (
        <p role="alert" className="mt-3 rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 dark:border-red-500/40 dark:text-red-300" data-testid="file-importer-problem">
          {t(`fileImporter.problems.${problem.code}`, { max: MAX_ROWS, columns: (problem.columns ?? []).join(', ') })}
        </p>
      ) : null}

      {reading || check.isPending ? (
        <p className="mt-3 inline-flex items-center gap-2 text-sm text-pw-text-muted" role="status" data-testid="file-importer-busy">
          <Spinner size="sm" />
          {t('fileImporter.checking')}
        </p>
      ) : null}

      {check.isError && rows ? (
        <div className="mt-3 flex items-center gap-2">
          <Button type="button" variant="outline" onClick={() => check.mutate(rows)} data-testid="file-importer-retry">
            {t('fileImporter.retry')}
          </Button>
        </div>
      ) : null}

      {report && rows ? (
        <div className="mt-3 space-y-2">
          <p aria-live="polite" className="text-sm text-pw-text" data-testid="file-importer-summary">
            {errors.length > 0
              ? t('fileImporter.summaryErrors', { errors: errors.length, warnings: warnings.length, rows: rows.length })
              : t('fileImporter.summaryOk', { products: report.products.length, rows: rows.length, warnings: warnings.length })}
          </p>
          <div className="max-h-[420px] overflow-auto rounded-md border border-pw-border">
            <table className="w-full border-collapse text-xs" data-testid="file-importer-table">
              <thead className="sticky top-0 bg-pw-bg-hover">
                <tr className="text-left text-pw-text-muted">
                  <th className="px-2 py-2">{t('fileImporter.colRow')}</th>
                  <th className="px-2 py-2">{t('fileImporter.colProduct')}</th>
                  <th className="px-2 py-2">{t('fileImporter.colPrintwaySku')}</th>
                  <th className="px-2 py-2">{t('fileImporter.colMatched')}</th>
                  <th className="px-2 py-2">{t('fileImporter.colIssues')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const issues = issuesByRow.get(r.row) ?? [];
                  const hasError = issues.some((i) => i.kind === 'error');
                  const code = String(r['product_code'] ?? '').toUpperCase();
                  return (
                    <tr
                      key={r.row}
                      className={cn('border-t border-pw-border', hasError && 'bg-red-500/5')}
                      data-testid={`file-importer-row-${r.row}`}
                      data-state={hasError ? 'error' : issues.length ? 'warning' : 'ok'}
                    >
                      <td className="px-2 py-1.5 tabular-nums text-pw-text-muted">{r.row}</td>
                      <td className="px-2 py-1.5 text-pw-text">{String(r['product_code'] ?? '—')}</td>
                      <td className="px-2 py-1.5 font-mono text-pw-text">{String(r['printway_sku'] ?? '—')}</td>
                      <td className="px-2 py-1.5 text-pw-text-muted">{blueprintOf.get(code) ?? '—'}</td>
                      <td className="px-2 py-1.5">
                        {issues.length === 0 ? (
                          <span className="text-pw-text-muted">✓ {t('fileImporter.ok')}</span>
                        ) : (
                          <ul className="space-y-0.5">
                            {issues.map((i, k) => (
                              <li
                                key={k}
                                className={i.kind === 'error' ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}
                              >
                                <span aria-hidden="true">{i.kind === 'error' ? '✕ ' : '! '}</span>
                                <span className="sr-only">{i.kind === 'error' ? t('fileImporter.errorLabel') : t('fileImporter.warningLabel')}: </span>
                                {i.text}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="mt-3 flex justify-end">
        <Button
          type="button"
          data-testid="file-importer-submit"
          onClick={() => rows && commit.mutate(rows)}
          disabled={!canImport}
        >
          {commit.isPending ? <Spinner size="sm" /> : null}
          {t('fileImporter.submit')}
        </Button>
      </div>
    </section>
  );
}

export default FileImporter;
